<template>
  <div class="settings-container mx-lg-4 mx-2">
    <h2 class="page-title">Configuración de cuenta</h2>
    <div class="settings-content">
        <!-- Toggle Buttons -->
        <div class="tabs-container">
          <button 
            class="tab-btn"
            :class="{ active: !showPasswordForm }"
            @click="showPasswordForm = false"
          >
            <i class="fas fa-user me-2"></i>
            Datos Personales
          </button>
          <button 
            class="tab-btn"
            :class="{ active: showPasswordForm }"
            @click="showPasswordForm = true"
          >
            <i class="fas fa-key me-2"></i>
            Cambiar Contraseña
          </button>
        </div>

        <!-- Personal Data Form -->
        <form v-if="!showPasswordForm" @submit.prevent="handleSubmit">
          <div class="form-grid">
            <div class="form-group">
              <label>Nombre</label>
              <input 
                v-model="formData.first_name" 
                type="text" 
                class="form-input"
                placeholder="Tu nombre"
              >
            </div>

            <div class="form-group">
              <label>Apellido</label>
              <input 
                v-model="formData.last_name" 
                type="text" 
                class="form-input"
                placeholder="Tu apellido"
              >
            </div>
            
            <div class="form-group full-width">
              <label>Email</label>
              <div class="email-field">
                <input 
                  v-model="formData.email" 
                  type="email" 
                  class="form-input"
                  placeholder="tu@email.com"
                  :disabled="isChangingEmail"
                >
                <button 
                  v-if="formData.email !== originalEmail && !isChangingEmail"
                  type="button" 
                  class="btn-verify"
                  @click="requestEmailChangeCode"
                >
                  <i class="fas fa-envelope me-2"></i>
                  Verificar
                </button>
              </div>
            </div>

            <div class="form-group">
              <label>DNI / CUIT</label>
              <input
                v-model="formData.dni"
                type="text"
                class="form-input"
                placeholder="Ej: 30111222 o 20-30111222-3"
              >
            </div>

            <div class="form-group">
              <label>Teléfono</label>
              <input
                v-model="formData.phone"
                type="text"
                class="form-input"
                placeholder="Ej: 2971234567"
              >
            </div>

            <div class="form-group full-width">
              <label>Instagram</label>
              <input
                v-model="formData.instagram"
                type="text"
                class="form-input"
                placeholder="@tuusuario o https://instagram.com/tuusuario"
              >
            </div>

            <!-- Email verification code input -->
            <div class="form-group full-width" v-if="isChangingEmail">
              <label>Código de verificación</label>
              <div class="verification-field">
                <input 
                  v-model="emailVerificationCode"
                  type="text"
                  class="form-input"
                  placeholder="Ingresa el código de 6 dígitos"
                  maxlength="6"
                >
                <button 
                  type="button" 
                  class="btn-primary"
                  @click="verifyAndUpdateEmail"
                  :disabled="emailVerificationCode.length !== 6"
                >
                  <i class="fas fa-check me-2"></i>
                  Verificar
                </button>
                <button 
                  type="button" 
                  class="btn-cancel-small"
                  @click="cancelEmailChange"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <small class="helper-text">Hemos enviado un código de verificación al nuevo email.</small>
            </div>
          </div>

          <!-- Email verification -->
          <div v-if="isChangingEmail" class="info-alert">
            <i class="fas fa-info-circle me-2"></i>
            Para cambiar tu email, ingresa el código de verificación enviado a <strong>{{ formData.email }}</strong>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="isLoading || isChangingEmail">
              <i class="fas fa-save me-2"></i>
              {{ isLoading ? 'Guardando...' : 'Guardar cambios' }}
            </button>
          </div>
        </form>

        <!-- Password Change Form -->
        <form v-else @submit.prevent="handlePasswordChange">
          <div class="form-grid">
            <div class="form-group full-width">
              <label>Contraseña Actual</label>
              <div class="password-field">
                <input 
                  :type="showCurrentPassword ? 'text' : 'password'" 
                  v-model="passwordForm.currentPassword" 
                  class="form-input"
                  placeholder="Ingresa tu contraseña actual"
                >
                <button 
                  type="button" 
                  class="btn-toggle-password"
                  @click="showCurrentPassword = !showCurrentPassword"
                >
                  <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
            
            <div class="form-group">
              <label>Nueva Contraseña</label>
              <div class="password-field">
                <input 
                  :type="showNewPassword ? 'text' : 'password'" 
                  v-model="passwordForm.newPassword" 
                  class="form-input"
                  placeholder="Ingresa tu nueva contraseña"
                >
                <button 
                  type="button" 
                  class="btn-toggle-password"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
            
            <div class="form-group">
              <label>Confirmar Nueva Contraseña</label>
              <div class="password-field">
                <input 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  v-model="passwordForm.confirmPassword" 
                  class="form-input"
                  placeholder="Confirma tu nueva contraseña"
                >
                <button 
                  type="button" 
                  class="btn-toggle-password"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="isChangingPassword">
              <i class="fas fa-key me-2"></i>
              {{ isChangingPassword ? 'Cambiando...' : 'Cambiar Contraseña' }}
            </button>
          </div>
        </form>
      </div>

    <!-- Password Verification Modal -->
    <div class="modal-overlay" v-if="showPasswordModal">
      <div class="password-modal">
        <div class="modal-header">
          <h5>Verificación de seguridad</h5>
          <button type="button" class="btn-close-modal" @click="cancelPasswordModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Por favor ingresa tu contraseña para continuar con el cambio de email.</p>
          <div class="form-group">
            <div class="password-field">
              <input 
                :type="showModalPassword ? 'text' : 'password'" 
                v-model="emailVerificationPassword"
                class="form-input" 
                placeholder="Tu contraseña actual" 
                @keyup.enter="submitPasswordModal"
              >
              <button 
                type="button" 
                class="btn-toggle-password"
                @click="showModalPassword = !showModalPassword"
              >
                <i :class="showModalPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
          </div>
          <div v-if="passwordModalError" class="error-alert">
            {{ passwordModalError }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="cancelPasswordModal">
            Cancelar
          </button>
          <button 
            class="btn-primary" 
            :disabled="!emailVerificationPassword || passwordModalLoading"
            @click="submitPasswordModal"
          >
            <i class="fas fa-spinner fa-spin me-2" v-if="passwordModalLoading"></i>
            {{ passwordModalLoading ? 'Verificando...' : 'Continuar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import { getUserInfo, updateUserProfile, changePassword, requestVerificationCode, updateEmailWithVerification } from '@/services/auth'
import { getCurrentDollarRate, createDollarRate, updateDollarRate, getDistributionCenters, createDistributionCenter, updateDistributionCenter, deleteDistributionCenter } from '@/services/payment'
import { getSiteConfig, updateSiteConfig } from '@/services/siteConfig'
import { useVuelidate } from '@vuelidate/core'
import { email, minLength, helpers, minValue, required } from '@vuelidate/validators'

const isAdmin = ref(localStorage.getItem('isAdmin') === 'true');
const toast = useToast()

// Move all the reactive state
const showPasswordForm = ref(false)
const isLoading = ref(false)
const originalEmail = ref('')
const isChangingEmail = ref(false)
const emailVerificationCode = ref('')
const newEmailAddress = ref('')
const emailVerificationPassword = ref('')
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const isChangingPassword = ref(false)
const showPasswordModal = ref(false)
const showModalPassword = ref(false)
const passwordModalLoading = ref(false)
const passwordModalError = ref('')
const pendingEmailChange = ref(false)

// Variables para centros de distribución, precio del dólar y configuración del sitio
const distributionCenters = ref([])
const isLoadingCenters = ref(false)
const isLoadingDollar = ref(false)
const currentDollarRateId = ref(null)
const isLoadingSiteConfig = ref(false)
const siteConfigId = ref(null)

const formData = reactive({
  first_name: '',
  last_name: '',
  email: '',
  dni: '',
  phone: '',
  instagram: '',
  storeEmail: '',
  storePhone: '',
  dollarPrice: '0.00',
  dollarUpdateDate: new Date().toISOString().split('T')[0],
  minPurchaseAmount: 0
})

const passwordForm = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// Move all the validation rules and functions
const rules = {
  first_name: { 
    minLength: helpers.withMessage('El nombre debe tener al menos 2 caracteres', minLength(2))
  },
  last_name: { 
    minLength: helpers.withMessage('El apellido debe tener al menos 2 caracteres', minLength(2))
  },
  email: {
    email: helpers.withMessage('Ingrese un email válido', email)
  },  
  dni: {
    minLength: helpers.withMessage('El DNI/CUIT debe tener al menos 7 caracteres', minLength(7))
  },
  phone: {
    minLength: helpers.withMessage('El teléfono debe tener al menos 6 caracteres', minLength(6))
  },
  instagram: {
    minLength: helpers.withMessage('El usuario de Instagram debe tener al menos 2 caracteres', minLength(2))
  },
  dollarPrice: {
    required: helpers.withMessage('El precio del dólar es requerido', required),
    minValue: helpers.withMessage('El precio del dólar debe ser al menos 0', minValue(0))
  },
  dollarUpdateDate: {
    required: helpers.withMessage('La fecha de actualización es requerida', required)
  },  minPurchaseAmount: {
    required: helpers.withMessage('El monto mínimo de compra es requerido', required),
    minValue: helpers.withMessage('El monto mínimo de compra debe ser al menos 0', minValue(0))
  },
  storeEmail: {
    email: helpers.withMessage('Ingrese un email válido', email)
  },
  storePhone: {
    minLength: helpers.withMessage('El teléfono debe tener al menos 6 caracteres', minLength(6))
  }
}

const v$ = useVuelidate(rules, formData)

// Move all the handler functions
const handleSubmit = async () => {
  try {
    const isFormValid = await v$.value.$validate()
    if (!isFormValid) {
      const firstError = Object.values(v$.value.$errors)[0]
      if (firstError) {
        toast.error(firstError.$message)
      }
      return
    }

    // Check if email has changed - if yes, we need verification
    if (formData.email !== originalEmail.value) {
      // Trigger email verification workflow
      requestEmailChangeCode()
      return
    }

    isLoading.value = true    // Create update object - only include fields that we want to update
    const updateData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      dni: (formData.dni || '').trim(),
      phone: (formData.phone || '').trim(),
      instagram: (formData.instagram || '').trim(),
      store_email: formData.storeEmail,
      store_phone: formData.storePhone
      // Email is handled separately with verification
    }

    // 1. Actualizar perfil de usuario
    await updateUserProfile(updateData)
      // 4. Actualizar configuración del sitio (mínimo de compra)
    try {
      const siteConfigData = {
        min_purchase_amount: parseFloat(formData.minPurchaseAmount) || 0
      };
      
      await updateSiteConfig(siteConfigData);
      console.log('Monto mínimo de compra actualizado:', siteConfigData.min_purchase_amount);
    } catch (error) {
      console.error('Error updating site configuration:', error);
      toast.error('Error al actualizar el monto mínimo de compra');
    }
      // 2. Actualizar tasa del dólar
    if (formData.dollarPrice) {
      try {        const dollarData = {
          value: formData.dollarPrice,
          is_active: true,
          update_date: new Date().toISOString() // Enviamos en formato ISO para que Django lo pueda interpretar correctamente
        };
        
        if (currentDollarRateId.value) {
          // Actualizar tasa existente
          await updateDollarRate(currentDollarRateId.value, dollarData);
        } else {
          // Crear nueva tasa
          const newRate = await createDollarRate(dollarData);
          currentDollarRateId.value = newRate.id;
        }
        
        // Actualizar fecha de actualización en la UI
        formData.dollarUpdateDate = new Date().toISOString().split('T')[0];
      } catch (error) {
        console.error('Error saving dollar rate:', error);
        toast.error('Error al actualizar la tasa del dólar');
      }
    }
    
    // 3. Procesar centros de distribución
    for (const center of distributionCenters.value) {
      try {
        if (center._deleted && center.id) {
          // Eliminar centro existente
          await deleteDistributionCenter(center.id);
        } else if (center.id) {
          // Actualizar centro existente
          const centerData = {
            name: center.name,
            address: center.address,
            phone: center.phone,
            is_active: center.active
          };
          await updateDistributionCenter(center.id, centerData);
        } else if (!center._deleted) {
          // Crear nuevo centro
          const centerData = {
            name: center.name || '',
            address: center.address || '',
            phone: center.phone || '',
            is_active: center.active
          };
          const newCenter = await createDistributionCenter(centerData);
          center.id = newCenter.id;
        }
      } catch (error) {
        console.error('Error processing distribution center:', error);
        toast.error(`Error al procesar el centro de distribución ${center.name || ''}`);
      }
    }
    
    // Eliminar centros marcados como eliminados
    distributionCenters.value = distributionCenters.value.filter(center => !center._deleted);
    
    toast.success('Perfil actualizado correctamente');
    
  } catch (error) {
    console.error('Error updating profile:', error)
    toast.error(error.error || 'Error al actualizar el perfil')
  } finally {
    isLoading.value = false
  }
}

const requestEmailChangeCode = async () => {
  // Store the new email address for verification
  newEmailAddress.value = formData.email
  
  // Show password modal instead of prompt
  showPasswordModal.value = true
  pendingEmailChange.value = true
}

const submitPasswordModal = async () => {
  if (!emailVerificationPassword.value) {
    passwordModalError.value = 'Por favor ingresa tu contraseña'
    return
  }

  passwordModalLoading.value = true
  passwordModalError.value = ''

  try {
    if (pendingEmailChange.value) {
      // Request verification code 
      await requestVerificationCode(
        'EMAIL_CHANGE', 
        formData.email,  // New email
        emailVerificationPassword.value  // Current password
      )

      // Close modal and show verification form
      showPasswordModal.value = false
      isChangingEmail.value = true
      toast.info('Código de verificación enviado al nuevo email')
    }
  } catch (error) {
    console.error('Error requesting code:', error)
    
    // Verificar tipos específicos de errores
    if (error.error === 'email_already_exists' || 
        (error.error && error.error.includes && error.error.includes('exist')) ||
        (typeof error === 'object' && error.message && error.message.includes('exist'))) {
      passwordModalError.value = 'Este email ya está registrado. Por favor utiliza otro email.'
      // Revertir el cambio de email en el formulario
      formData.email = originalEmail.value
    } else if (error.error === 'invalid_password' || 
               (error.error && error.error.includes && error.error.includes('contraseña')) ||
               (error.error && error.error.includes && error.error.includes('password'))) {
      passwordModalError.value = 'Contraseña incorrecta'
    } else {
      passwordModalError.value = error.error || error.message || 'Error al solicitar código de verificación'
    }
  } finally {
    passwordModalLoading.value = false
    // Solo cambiar pendingEmailChange si no hay error de email existente
    if (!passwordModalError.value.includes('ya está registrado')) {
      pendingEmailChange.value = false
    }
  }
}

const cancelPasswordModal = () => {
  showPasswordModal.value = false
  emailVerificationPassword.value = ''
  passwordModalError.value = ''
  pendingEmailChange.value = false
  
  // Revert email change if cancelling
  if (pendingEmailChange.value) {
    formData.email = originalEmail.value
  }
}

const verifyAndUpdateEmail = async () => {
  if (emailVerificationCode.value.length !== 6) {
    toast.error('El código debe tener 6 dígitos')
    return
  }

  try {
    isLoading.value = true
    await updateEmailWithVerification(newEmailAddress.value, emailVerificationCode.value)
    
    // Update original email after successful change
    originalEmail.value = newEmailAddress.value
    
    // Reset verification state
    isChangingEmail.value = false
    emailVerificationCode.value = ''
    
    toast.success('Email actualizado correctamente')
  } catch (error) {
    console.error('Error al actualizar email:', error)
    // Verificamos si es un error específico de email ya registrado
    if (error.error === 'email_already_exists' || 
        (error.error && error.error.includes && error.error.includes('exist')) || 
        (error.message && error.message.includes && error.message.includes('exist'))) {
      toast.error('Este email ya está registrado. Por favor utiliza otro email.')
    } else if (error.error === 'invalid_code' || 
              (error.error && error.error.includes && error.error.includes('código')) ||
              (error.error && error.error.includes && error.error.includes('code'))) {
      toast.error('El código de verificación es inválido o ha expirado')
    } else {
      toast.error(error.error || error.message || 'Error al actualizar el email')
    }
  } finally {
    isLoading.value = false
  }
}

const cancelEmailChange = () => {
  // Restore original email
  formData.email = originalEmail.value
  isChangingEmail.value = false
  emailVerificationCode.value = ''
  newEmailAddress.value = ''
}

const handlePasswordChange = async () => {
  // Validaciones
  if (!passwordForm.currentPassword) {
    toast.error('Ingresa tu contraseña actual')
    return
  }
  
  if (!passwordForm.newPassword) {
    toast.error('Ingresa la nueva contraseña')
    return
  }

  if (passwordForm.newPassword.length < 6) {
    toast.error('La nueva contraseña debe tener al menos 6 caracteres')
    return
  }

  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
    toast.error('Las contraseñas no coinciden')
    return
  }

  try {
    isChangingPassword.value = true
    
    await changePassword({
      old_password: passwordForm.currentPassword,
      new_password: passwordForm.newPassword
    })

    toast.success('Contraseña actualizada correctamente')
    
    // Limpiar formulario
    passwordForm.currentPassword = ''
    passwordForm.newPassword = ''
    passwordForm.confirmPassword = ''
    showPasswordForm.value = false

  } catch (error) {
    console.error('Error changing password:', error)
    const errorMessage = error.response?.data?.error || 'Error al cambiar la contraseña'
    toast.error(errorMessage)
  } finally {
    isChangingPassword.value = false
  }
}

onMounted(async () => {
  try {
    // Cargar datos del usuario
    const userData = await getUserInfo();
    formData.first_name = userData.first_name || '';
    formData.last_name = userData.last_name || '';
    formData.email = userData.email || '';
    formData.dni = userData.dni || '';
    formData.phone = userData.phone || '';
    formData.instagram = userData.instagram || '';
    originalEmail.value = userData.email || '';
    
    if (isAdmin.value) {
      // Cargar tasa del dólar
      isLoadingDollar.value = true;
      try {
        const dollarRate = await getCurrentDollarRate();
        if (dollarRate) {
          formData.dollarPrice = dollarRate.value;
          formData.dollarUpdateDate = new Date(dollarRate.update_date || dollarRate.updated_at).toISOString().split('T')[0];
          currentDollarRateId.value = dollarRate.id;
        }
      } catch (error) {
        console.error('Error loading dollar rate:', error);
      } finally {
        isLoadingDollar.value = false;
      }
      
      // Cargar centros de distribución
      isLoadingCenters.value = true;
      try {
        const centers = await getDistributionCenters();
        distributionCenters.value = centers.map(center => ({
          ...center,
          active: center.is_active
        }));
      } catch (error) {
        console.error('Error loading distribution centers:', error);
      } finally {
        isLoadingCenters.value = false;
      }
      
      // Cargar configuración del sitio
      isLoadingSiteConfig.value = true;
      try {
        const siteConfig = await getSiteConfig();
        if (siteConfig) {
          formData.minPurchaseAmount = parseFloat(siteConfig.min_purchase_amount) || 0;
          formData.storeEmail = siteConfig.email || '';
          formData.storePhone = siteConfig.phone || '';
          siteConfigId.value = siteConfig.id;
        }
      } catch (error) {
        console.error('Error loading site configuration:', error);
        toast.error('No se pudo cargar la configuración de la tienda');
      } finally {
        isLoadingSiteConfig.value = false;
      }
    }
  } catch (error) {
    console.error('Error loading data:', error);
    toast.error('Error al cargar los datos');
  }
})
</script>

<style scoped>
.settings-container {
  padding: 0;
}

.page-title {
  color: #ccc;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0 0 1.25rem 0;
}

.settings-content {
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  padding: 1.25rem;
}

.tabs-container {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.tab-btn {
  padding: 0.5rem 1rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #aaa;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
}
.tab-btn:hover {
  background: rgba(var(--primary-color-rgb), 0.15);
  border-color: rgba(var(--primary-light-rgb), 0.25);
  color: #ccc;
}
.tab-btn.active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}
.form-group {
  margin-bottom: 0;
}
.form-group.full-width {
  grid-column: 1 / -1;
}

label {
  display: block;
  color: #888;
  font-size: 0.82rem;
  margin-bottom: 0.4rem;
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: 0.55rem 0.85rem;
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #ccc;
  font-size: 0.88rem;
  transition: border-color 0.15s ease;
}
.form-input::placeholder {
  color: #666;
}
.form-input:focus {
  outline: none;
  border-color: rgba(var(--primary-light-rgb), 0.4);
}
.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.email-field,
.password-field,
.verification-field {
  display: flex;
  gap: 0.5rem;
}
.email-field .form-input,
.password-field .form-input,
.verification-field .form-input {
  flex: 1;
}

.btn-verify {
  padding: 0.55rem 0.85rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #aaa;
  font-size: 0.84rem;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.btn-verify:hover {
  background: rgba(var(--primary-color-rgb), 0.2);
  border-color: rgba(var(--primary-light-rgb), 0.3);
  color: var(--primary-light);
}

.btn-toggle-password {
  padding: 0.55rem 0.75rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #888;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-toggle-password:hover {
  color: #ccc;
  background: rgba(0,0,0,0.3);
}

.btn-cancel-small {
  padding: 0.55rem 0.75rem;
  background: rgba(248,113,113,0.15);
  border: 1px solid rgba(248,113,113,0.25);
  border-radius: 8px;
  color: #f87171;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-cancel-small:hover {
  background: rgba(248,113,113,0.25);
}

.helper-text {
  display: block;
  color: #666;
  font-size: 0.78rem;
  margin-top: 0.35rem;
}

.info-alert {
  background: rgba(96,165,250,0.12);
  border: 1px solid rgba(96,165,250,0.25);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #60a5fa;
  font-size: 0.84rem;
  margin-top: 1rem;
}

.error-alert {
  background: rgba(248,113,113,0.12);
  border: 1px solid rgba(248,113,113,0.25);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  color: #f87171;
  font-size: 0.84rem;
  margin-top: 0.75rem;
}

.form-actions {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255,255,255,0.06);
}

.btn-primary {
  padding: 0.55rem 1.1rem;
  background: var(--primary-color);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background 0.15s ease;
  display: inline-flex;
  align-items: center;
}
.btn-primary:hover:not(:disabled) {
  background: var(--primary-dark);
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.password-modal {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  width: 100%;
  max-width: 420px;
  margin: 1rem;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.modal-header h5 {
  margin: 0;
  color: #ccc;
  font-size: 1rem;
  font-weight: 600;
}

.btn-close-modal {
  background: none;
  border: none;
  color: #888;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.15s ease;
}
.btn-close-modal:hover {
  color: #ccc;
}

.modal-body {
  padding: 1.25rem;
}
.modal-body p {
  color: #aaa;
  font-size: 0.88rem;
  margin: 0 0 1rem 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid rgba(255,255,255,0.06);
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  color: #aaa;
  font-size: 0.84rem;
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn-cancel:hover {
  background: rgba(0,0,0,0.3);
  color: #ccc;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
  .form-group.full-width {
    grid-column: 1;
  }
  .tabs-container {
    flex-direction: column;
  }
  .tab-btn {
    justify-content: center;
  }
  .email-field,
  .verification-field {
    flex-direction: column;
  }
}
</style>